import { Building2, CheckCircle2, Clock } from 'lucide-vue-next'
import ColumnLink from 'pejer/src/components/Table/core/ColumnLink.vue'
import { Groups, JSSchema } from 'pejer/src/tables'
import { ColorPalette } from 'pejer/src/tables/helpers'
import { h } from 'vue'

export type Full = ListItem

export enum CompanyStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}

export const CompanyStatusList = [CompanyStatus.ACTIVE, CompanyStatus.INACTIVE] as const

export interface ListItem {
  id: number
  organizationId: string
  countryCode: string
  name: string
  procurationRoles: string
  signatoryRoles: string
  signatories: string
  companyTypeName: string
  companyType: string
  companyStatus: CompanyStatus | null
  MCC: string | null

  website: string | null
  tosAcceptedAt: Date | null
  signatureCompletedAt: Date | null

  updateTime: Date
  insertTime: Date
}

export const BusinessGroups = {
  Business: {
    icon: Building2,
    color: ColorPalette.red,
    isMainGroup: true,
    mainColumn: 'name',
  },
  'TOS Accepted at': {
    icon: CheckCircle2,
    color: ColorPalette.orange,
    mainColumn: 'tosAcceptedAt',
  },
  Time: {
    icon: Clock,
    color: ColorPalette.black,
    mainColumn: 'insertTime',
  },
} satisfies Groups<ListItem>

export const BusinessListSchema: JSSchema<ListItem, typeof BusinessGroups> = {
  name: 'Business',
  title: 'Businesses',
  groups: BusinessGroups,
  fields: {
    id: {
      type: 'number',
      humanReadable: 'ID',
      meta: {
        group: 'Business',
      },
    },
    countryCode: {
      type: 'string',
      humanReadable: 'Country Code',
      meta: {
        group: 'Business',
      },
      metaType: 'countryCode',
    },
    organizationId: {
      type: 'string',
      humanReadable: 'Organization ID',
      meta: {
        group: 'Business',
      },
    },
    name: {
      type: 'string',
      humanReadable: 'Name',
      meta: {
        group: 'Business',
      },
      specificCell:
        (self) =>
        ({ row }) => {
          return h(
            ColumnLink,
            {
              id: {
                countryCode: row.original.countryCode,
                organizationId: row.original.organizationId,
              },
              pathName: 'business',
              content: row.original.name,
            },
            () => row.original.name
          )
        },
    },
    companyTypeName: {
      type: 'string',
      humanReadable: 'Company Type Name',
      meta: {
        group: 'Business',
      },
    },
    companyType: {
      type: 'string',
      humanReadable: 'Company Type',
      meta: {
        group: 'Business',
      },
    },
    companyStatus: {
      type: 'enum',
      humanReadable: 'Company Status',
      optional: true,
      meta: {
        group: 'Business',
      },
      options: CompanyStatusList,
    },
    signatories: {
      type: 'string',
      humanReadable: 'Signatories',
      meta: {
        group: 'Business',
      },
      metaType: 'array',
      formatter: {
        from: (value: string) => JSON.parse(value),
      },
    },
    signatoryRoles: {
      type: 'string',
      humanReadable: 'Signatory Roles',
      meta: {
        group: 'Business',
      },
      metaType: 'array',
      formatter: {
        from: (value: string) => JSON.parse(value).map((v: { Name: string }) => v.Name),
      },
    },
    procurationRoles: {
      type: 'string',
      humanReadable: 'Procuration Roles',
      meta: {
        group: 'Business',
      },
      metaType: 'array',
      formatter: {
        from: (value: string) => JSON.parse(value).map((v: { Name: string }) => v.Name),
      },
    },
    website: {
      type: 'string',
      humanReadable: 'Website',
      meta: {
        group: 'Business',
      },
      metaType: 'url',
    },
    tosAcceptedAt: {
      type: 'date',
      humanReadable: 'TOS Accepted At',
      meta: {
        group: 'TOS Accepted at',
      },
    },
    signatureCompletedAt: {
      type: 'date',
      humanReadable: 'Signature Completed At',
      meta: {
        group: 'TOS Accepted at',
      },
    },
    MCC: {
      type: 'string',
      humanReadable: 'MCC',
      meta: {
        group: 'Business',
      },
    },
    updateTime: {
      type: 'date',
      humanReadable: 'Update Time',
      meta: {
        group: 'Time',
      },
    },
    insertTime: {
      type: 'date',
      humanReadable: 'Insert Time',
      meta: {
        group: 'Time',
      },
      isDefaultSortColumn: true,
    },
  },
}

export const BusinessFullSchema: JSSchema<Full, typeof BusinessGroups> = {
  ...BusinessListSchema,
  title: 'Business',
}
